import { useEffect, useMemo, useState, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import { Shell } from "../../common/shell/shell.page";
import { PageHeader } from "../../common/components/page-header";
import { LoadingOverlay } from "../../common/components/loading-overlay";
import application from "../../core/application";
import { Permission } from "../../users/user.entity";
import { Customer } from "../customer.entity";
import { customerRepository } from "../customer.repository";
import { customerAccountRepository } from "../customer-account.repository";
import { CustomerAccount } from "../customer-account.entity";
import { formatBRL } from "../../sell/utils/sales-analytics";
import { useModuleAccess } from "../../settings/module-access";
import { ModuleLockedView } from "../../settings/components/module-locked-view";

export const CreditReportPage = () => {
  application.useHasPermission(Permission.MANAGE_CUSTOMERS);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [accounts, setAccounts] = useState<Map<string, CustomerAccount>>(new Map());
  const moduleAccess = useModuleAccess();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await customerRepository.listByName(500);
        const accountMap = await customerAccountRepository.listByCustomerIds(
          list.map((customer) => customer.id)
        );
        if (!cancelled) {
          setCustomers(list);
          setAccounts(accountMap);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { debtors, stats } = useMemo(() => {
    const rows = customers.map((customer) => ({
      customer,
      balance: accounts.get(customer.id)?.getBalance() ?? 0,
    }));
    
    // Only customers with debt
    const debtorsList = rows
      .filter((row) => row.balance > 0)
      .sort((a, b) => b.balance - a.balance); // Highest debt first

    const totalDebt = debtorsList.reduce((sum, row) => sum + row.balance, 0);

    return {
      debtors: debtorsList,
      stats: {
        totalCustomers: customers.length,
        debtorsCount: debtorsList.length,
        totalDebt,
        averageDebt: debtorsList.length ? totalDebt / debtorsList.length : 0,
      },
    };
  }, [accounts, customers]);

  if (moduleAccess.loading) {
    return (
      <Shell title="Relatório de Fiado">
        <LoadingOverlay message="Carregando módulo…" subtle />
      </Shell>
    );
  }

  if (!moduleAccess.activeCustomersTier) {
    return (
      <Shell title="Relatório de Fiado">
        <PageHeader title="Relatório de Fiado" description="Visão detalhada de devedores." />
        <ModuleLockedView
          moduleName="Clientes e Fiado"
          groupId="customers_credit"
          details="Ative este módulo para acessar relatórios detalhados de fiado."
          features={["Listagem de devedores", "Saldos em aberto", "Visão geral de fiado"]}
        />
      </Shell>
    );
  }

  return (
    <Shell title="Relatório de Fiado">
      {loading ? <LoadingOverlay message="Carregando devedores…" subtle /> : null}

      <PageHeader
        title="Relatório de Fiado"
        description="Lista detalhada de clientes com saldo devedor."
        action={
          <Button variant="outlined" component={RouterLink} to="/clientes" startIcon={<ArrowBackIcon />}>
            Voltar para Clientes
          </Button>
        }
      />

      <Stack spacing={3}>
        <PaperLikeHero stats={stats} />

        <Card elevation={0}>
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 2.5, py: 2, borderBottom: 1, borderColor: "divider" }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Clientes com Dívida
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {debtors.length} de {stats.totalCustomers} clientes possuem saldo devedor.
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06) }}>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Saldo Devedor</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {debtors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4}>
                        <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                          Nenhum cliente com dívida no momento.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    debtors.map(({ customer, balance }) => (
                      <TableRow key={customer.id} hover>
                        <TableCell>
                          <Stack direction="row" spacing={1.25} alignItems="center">
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: "warning.main",
                                color: "primary.contrastText",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <PersonIcon />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography fontWeight={600} noWrap title={customer.getName()}>
                                {customer.getName()}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {customer.getPhone() || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color="warning.dark">
                            {formatBRL(balance)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            component={RouterLink}
                            to={`/clientes/${customer.id}`}
                          >
                            Detalhes
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Stack>
    </Shell>
  );
};

function PaperLikeHero({
  stats,
}: {
  stats: {
    totalCustomers: number;
    debtorsCount: number;
    totalDebt: number;
    averageDebt: number;
  };
}) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.warning.main, 0.22),
        background: `linear-gradient(135deg, ${alpha(
          theme.palette.warning.main,
          0.1
        )} 0%, ${alpha(theme.palette.background.paper, 1)} 100%)`,
        p: { xs: 2, sm: 2.5 },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.warning.main, 0.15),
            color: "warning.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <AccountBalanceWalletIcon />
        </Box>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.08 }}>
            Resumo
          </Typography>
          <Typography variant="h6" fontWeight={800}>
            Visão geral de devedores
          </Typography>
        </Box>
      </Stack>
      <Grid container spacing={1.5}>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Clientes com dívida" value={String(stats.debtorsCount)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Total da carteira" value={String(stats.totalCustomers)} />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat
            label="Total em aberto"
            value={formatBRL(stats.totalDebt)}
            emphasize={stats.totalDebt > 0 ? "warning" : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <MiniStat label="Ticket médio devido" value={formatBRL(stats.averageDebt)} />
        </Grid>
      </Grid>
    </Box>
  );
}

function MiniStat({
  label,
  value,
  emphasize,
  icon,
}: {
  label: string;
  value: string;
  emphasize?: "error" | "warning";
  icon?: ReactNode;
}) {
  const color =
    emphasize === "error" ? "error.main" : emphasize === "warning" ? "warning.dark" : "text.primary";
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        {icon}
        <Typography variant="h6" fontWeight={800} sx={{ color }}>
          {value}
        </Typography>
      </Stack>
    </Box>
  );
}
